import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { useSelector } from 'react-redux';
import { dimensionsListSelectorKeyValue } from '@store/common/dimensionList/selectors';
import { isMetricFilterAppliedSelector } from '@store/trends/base/selectors';
import {
  getPercentageLocalKeyFromPrimaryKey,
  getMetricMetaByKey,
  getPercentageMetricByKey,
  getPercentileMetrics,
} from '@utils/metrics/metricHelpers';
import { getMetricFilterMetric, getMetricFilterPctMetric } from '../dimension-table/utils';
import { buildIntervalText } from '@components/export/parsers';
import { MF_LABEL } from '@components/metrics-panel-hz/utils/metricFilterUtils';
import {
  chartIntervalSelector,
  tableCompareIntervalSelector,
  noBrushSelector,
} from '@store/trends/birdChart/selectors';
import { getCompareIntervalLabel } from '@src/components/dimension-panel-hz/utils';
import { dodAtom, percentileAtom } from '@components/metrics-panel-hz/atoms';
import { isTableCompareEnabledAtom } from '@src/components/dimension-panel-hz/atoms/index';
import useSetExport from '@components/export/useSetExport';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';
import { percentileConfig } from '@utils/metrics/constants';

function useSetDimensionExport({ dimension, metrics, data = [], totals = {}, showNAForMetric }) {
  const dimensions: any = useSelector(dimensionsListSelectorKeyValue);
  const { flowId, frustrationSignalId } = useDrilldownMeta();
  const isMetricFilterApplied = useSelector(isMetricFilterAppliedSelector);
  const interval = useSelector(chartIntervalSelector);
  const percentile = useAtomValue(percentileAtom);

  const isTableCompareEnabled = useAtomValue(isTableCompareEnabledAtom);

  const compareInterval = useSelector(tableCompareIntervalSelector);
  const dodEnabled = useAtomValue(dodAtom);
  const isSliceAvailable = !useSelector(noBrushSelector);

  const headers = useMemo(() => {
    const headers: any = [
      {
        key: 'name',
        title: '',
        type: 'dimension',
      },
    ];

    // Get percentile prefix if not 'avg'
    const prefix =
      percentile !== 'avg' && percentileConfig[percentile]
        ? `${percentileConfig[percentile].short} - `
        : '';

    try {
      metrics.forEach((metric) => {
        const metricInfo = getMetricMetaByKey({
          metricKey: metric,
          flowOverride: !!flowId,
          frustrationSignalOverride: !!frustrationSignalId,
        });
        const pctMetric = getPercentageMetricByKey({ metricKey: metric, defaultValue: metric });
        const pctTotalMetric = getPercentageLocalKeyFromPrimaryKey({
          metricKey: metric,
          defaultValue: metric,
        });
        const type = !!showNAForMetric && showNAForMetric(metric) ? 'notApplicable' : null;

        // Check if this metric is a percentile metric
        const isPercentileMetric = getPercentileMetrics().includes(metric);

        // Add percentile prefix to metric title if applicable
        const metricTitle =
          isPercentileMetric && prefix ? `${prefix}${metricInfo.short}` : metricInfo.short;

        // base metric
        headers.push({ key: metric, title: metricTitle, type });

        // metric filter metric
        if (isMetricFilterApplied) {
          headers.push({
            key: getMetricFilterMetric(metric),
            title: `[${MF_LABEL}] ` + metricTitle,
            type,
          });
        }

        if (isTableCompareEnabled) {
          const compareLabel = getCompareIntervalLabel({
            isSliceAvailable,
            dodEnabled,
            compareInterval,
          });
          headers.push({
            key: getMetricFilterMetric(metric),
            title: compareLabel,
            type,
          });
        }

        // percentage metric
        if (metric !== pctMetric) {
          headers.push({ key: pctMetric, title: metricTitle + ' (%)', type });

          // percentage metric filter metric
          if (isMetricFilterApplied) {
            headers.push({
              key: getMetricFilterMetric(pctMetric),
              title: `[${MF_LABEL}] ` + metricTitle + ' (%)',
              type,
            });
          }

          if (isTableCompareEnabled) {
            const compareLabel = getCompareIntervalLabel({
              isSliceAvailable,
              dodEnabled,
              compareInterval,
            });
            headers.push({
              key: getMetricFilterMetric(pctMetric),
              title: compareLabel,
              type,
            });
          }
        }

        // MF relative % metric
        if (isMetricFilterApplied) {
          headers.push({
            key: getMetricFilterPctMetric(metric),
            title: `[${MF_LABEL}%] ` + metricTitle + ' (%)',
            type,
          });
        }

        // percentage of total metric
        if (metric !== pctTotalMetric) {
          headers.push({ key: pctTotalMetric, title: metricTitle + ' (%T)', type });
        }

        if (isTableCompareEnabled) {
          const compareLabel = getCompareIntervalLabel({
            isSliceAvailable,
            dodEnabled,
            compareInterval,
          });
          headers.push({
            key: getMetricFilterPctMetric(metric),
            title: `${compareLabel} ` + metricTitle + ' (%)',
            type,
          });
        }
      });
    } catch (err) {
      // pass
    }

    return headers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    metrics,
    data,
    percentile,
    flowId,
    frustrationSignalId,
    isMetricFilterApplied,
    isTableCompareEnabled,
    compareInterval,
    dodEnabled,
    isSliceAvailable,
    showNAForMetric,
  ]); // applying MF doesn't change metrics list, data dep will refresh headers

  useSetExport({
    pageKey: 'trends',
    sectionKey: 'dimensions-panel',
    widgetKey: `dimension-table::${dimension}`,
    headers,
    data: [totals].concat(data),
    widgetInfo: {
      title: `Dimension: ${dimensions[dimension]} (${buildIntervalText(interval)})`,
    },
  });
}

export default useSetDimensionExport;
